import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Skeleton,
    IconButton,
    Tooltip,
    Link as MuiLink,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CustomizedSnackbars from '../material-snackbars';
import { getExternalReferencesByInternal } from '../../services/webhook.service';

// Aba "Integracoes Externas" (S3.3) — lista as correlacoes entre a entidade Mexx atual
// (chamado por padrao, pode servir chat/user no futuro) e entidades externas capturadas
// via webhook outbound (response) ou app_connector inbound. Read-only por enquanto —
// exclusao/edit feita nas respectivas configuracoes de origem.
class CalledExternalReferences extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            references: [],
            notification: { open: false, variant: 'success', message: '' },
        };
        this.reload = this.reload.bind(this);
        this.copyValue = this.copyValue.bind(this);
    }

    componentDidMount() {
        this.reload();
    }

    componentDidUpdate(prevProps) {
        if (this.props.calledId !== prevProps.calledId || this.props.entityType !== prevProps.entityType) {
            this.reload();
        }
    }

    async reload() {
        const { calledId, entityType } = this.props;
        if (!calledId) { this.setState({ loading: false, references: [] }); return; }
        this.setState({ loading: true });
        try {
            const res = await getExternalReferencesByInternal(entityType || 'called', calledId);
            if (res.success && Array.isArray(res.data)) {
                this.setState({ references: res.data });
            } else {
                this.setState({ references: [] });
            }
        } catch (err) {
            this.showNotification('error', err.message);
        } finally {
            this.setState({ loading: false });
        }
    }

    copyValue(text) {
        if (!text) return;
        try {
            navigator.clipboard.writeText(text);
            this.showNotification('success', this.props.intl.formatMessage({ id: 'copied' }));
        } catch {
            // fallback silencioso
        }
    }

    showNotification(variant, message) {
        this.setState({ notification: { open: true, variant, message } });
    }
    closeNotification = () => this.setState(prev => ({ notification: { ...prev.notification, open: false } }));

    // Origem legivel (evita mostrar snake_case cru na UI).
    getOriginLabel(originModule) {
        const { intl } = this.props;
        switch (originModule) {
            case 'webhook_outbound': return intl.formatMessage({ id: 'external.ref.origin.webhook' });
            case 'appconnector_inbound': return intl.formatMessage({ id: 'external.ref.origin.appconnector' });
            case 'automation': return intl.formatMessage({ id: 'external.ref.origin.automation' });
            case 'manual': return intl.formatMessage({ id: 'external.ref.origin.manual' });
            default: return originModule || '-';
        }
    }

    // Cor do chip por origem (visual quick-scan).
    getOriginColor(originModule) {
        switch (originModule) {
            case 'webhook_outbound': return { backgroundColor: '#1976d2', color: 'white' };
            case 'appconnector_inbound': return { backgroundColor: '#7b1fa2', color: 'white' };
            case 'automation': return { backgroundColor: '#f57c00', color: 'white' };
            case 'manual': return { backgroundColor: '#757575', color: 'white' };
            default: return {};
        }
    }

    // Se o valor externo pareceu ser uma URL, ativa link "abrir externo".
    // Nao temos base_url por provider ainda — usamos apenas quando externalentityid ja e URL.
    tryExternalLink(value) {
        if (!value) return null;
        const v = String(value).trim();
        if (v.startsWith('http://') || v.startsWith('https://')) return v;
        return null;
    }

    renderEmpty() {
        const { intl } = this.props;
        return (
            <Box sx={{
                textAlign: 'center', padding: 4,
                border: '1px dashed #e0e0e0', borderRadius: 2,
            }}>
                <LinkOffIcon sx={{ fontSize: 56, color: '#bdbdbd' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
                    {intl.formatMessage({ id: 'external.ref.empty.title' })}
                </Typography>
                <Typography variant="body2" sx={{ color: '#888', maxWidth: 500, margin: '4px auto 0' }}>
                    {intl.formatMessage({ id: 'external.ref.empty.desc' })}
                </Typography>
            </Box>
        );
    }

    render() {
        const { intl } = this.props;
        const { loading, references, notification } = this.state;

        return (
            <Box sx={{ padding: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    {intl.formatMessage({ id: 'external.ref.title' })}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {intl.formatMessage({ id: 'external.ref.desc' })}
                </Typography>

                {loading && (
                    <>
                        <Skeleton variant="rectangular" height={44} sx={{ mb: 1 }} animation="wave" />
                        <Skeleton variant="rectangular" height={44} sx={{ mb: 1 }} animation="wave" />
                        <Skeleton variant="rectangular" height={44} animation="wave" />
                    </>
                )}

                {!loading && references.length === 0 && this.renderEmpty()}

                {!loading && references.length > 0 && (
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{intl.formatMessage({ id: 'external.ref.entity.type' })}</TableCell>
                                    <TableCell>{intl.formatMessage({ id: 'external.ref.entity.id' })}</TableCell>
                                    <TableCell>{intl.formatMessage({ id: 'external.ref.origin' })}</TableCell>
                                    <TableCell>{intl.formatMessage({ id: 'external.ref.captured.at' })}</TableCell>
                                    <TableCell align="right">{intl.formatMessage({ id: 'actions' })}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {references.map(r => {
                                    const link = this.tryExternalLink(r.externalentityid);
                                    return (
                                        <TableRow key={r.id} hover>
                                            <TableCell>
                                                <Chip size="small" variant="outlined" label={r.externalentitytype} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                    {r.externalentityid}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={this.getOriginLabel(r.originmodule)} style={this.getOriginColor(r.originmodule)} />
                                            </TableCell>
                                            <TableCell>
                                                {r.createdat ? moment(r.createdat).format('DD/MM/YYYY HH:mm') : '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={intl.formatMessage({ id: 'copy' })}>
                                                    <IconButton size="small" onClick={() => this.copyValue(r.externalentityid)}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {link && (
                                                    <Tooltip title={intl.formatMessage({ id: 'external.ref.open' })}>
                                                        <IconButton size="small" component={MuiLink} href={link} target="_blank" rel="noopener noreferrer">
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                <CustomizedSnackbars
                    variant={notification.variant}
                    message={notification.message}
                    isOpen={notification.open}
                    toClose={this.closeNotification}
                />
            </Box>
        );
    }
}

CalledExternalReferences.propTypes = {
    calledId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    entityType: PropTypes.string,
    intl: PropTypes.object.isRequired,
};

CalledExternalReferences.defaultProps = {
    entityType: 'called',
};

export default injectIntl(CalledExternalReferences);
